import React, { useState, useEffect, useRef, useCallback } from "react";
import { URLS } from "../Url";

const occDesc = n => ({
  "Anniversary":       "Celebrate your love in a private, luxurious theatre with personalised ambiance and exceptional quality.",
  "Bride To Be":       "Special private screening for the bride-to-be — personalised decor, scrumptious food, luxurious experience.",
  "Groom To Be":       "Make the groom's last bachelor night memorable with a private screening and intimate theatre experience.",
  "Dad To Be":         "Celebrate the soon-to-be father in a private setting with custom decor and exceptional service.",
  "Mom To Be":         "Celebrate the expecting mother with amazing decor, personalised services, and a relaxing experience.",
  "Love Proposal":     "The perfect surprise proposal — rose petals, candles, and a personalised message on the big screen.",
  "Marriage Proposal": "Make it official the most cinematic way. Custom decor, your favourite song, frozen in time.",
  "Farewell":          "Bid farewell in style with a private screening and the perfect personalised ambiance.",
  "Bon voyage":        "Send off a loved one with a special private screening, gourmet food and personalised decor.",
}[n] || "CarnivalCastle Private Theatres — intimate, luxurious and unforgettable for every special occasion.");

const SPARKLE_POS = [[15,20],[80,15],[20,70],[75,80],[50,10],[90,50],[10,50],[60,85],[30,35],[70,30]];

function OccCard({ occ, navigate }) {
  const cardRef = useRef(null);
  const zoneRef = useRef(null);

  const addSparkles = () => {
    if (!zoneRef.current) return;
    zoneRef.current.innerHTML = "";
    SPARKLE_POS.forEach(([l, t], i) => {
      const sp = document.createElement("div");
      sp.className = "sparkle";
      const s = 5 + Math.random() * 5;
      sp.style.cssText = `left:${l}%;top:${t}%;width:${s}px;height:${s}px;animation-delay:${i * 0.12}s;animation-duration:${1.2 + Math.random() * .6}s;`;
      zoneRef.current.appendChild(sp);
    });
  };
  const clearSparkles = () => { if (zoneRef.current) zoneRef.current.innerHTML = ""; };

  const handleTouchStart = () => {
    document.querySelectorAll(".occ-card.touched").forEach(c => {
      if (c !== cardRef.current) { c.classList.remove("touched"); const z = c.querySelector(".sparkle-zone"); if (z) z.innerHTML = ""; }
    });
    const already = cardRef.current.classList.contains("touched");
    if (already) { cardRef.current.classList.remove("touched"); clearSparkles(); }
    else { cardRef.current.classList.add("touched"); addSparkles(); }
  };

  return (
    <div ref={cardRef} className="occ-card"
      onMouseEnter={addSparkles} onMouseLeave={clearSparkles}
      onTouchStart={handleTouchStart} onTouchEnd={e => e.preventDefault()}>
      <div className="sparkle-zone" ref={zoneRef} />
      <img src={URLS.Base + occ.image} alt={occ.name} loading="lazy" />
      <div className="occ-always"><div className="occ-title-always">{occ.name}</div></div>
      <div className="occ-ovl">
        <div className="occ-ovl-inner">
          <div className="occ-occ-name">{occ.name}</div>
          <p className="occ-desc">{occDesc(occ.name)}</p>
          <button className="occ-book-gold" onClick={() => navigate("/locations")}>
            <span className="occ-book-shine" />
            <span className="occ-book-icon">🎉</span>
            <span className="occ-book-label">Book Now</span>
            <span className="occ-book-arr">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OccasionsSection({ occasions, navigate }) {
  const trackRef = useRef(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const autoRef = useRef(null);

  const getPerView = () => window.innerWidth < 769 ? 1 : window.innerWidth < 1025 ? 2 : 3;

  const applyTransform = useCallback((idx) => {
    const track = trackRef.current;
    if (!track) return;
    const cards = track.querySelectorAll(".occ-card");
    if (!cards.length) return;
    track.style.transform = `translateX(-${idx * (cards[0].offsetWidth + 20)}px)`;
  }, []);

  const goTo = useCallback((i) => {
    const max = Math.max(0, occasions.length - getPerView());
    const next = Math.max(0, Math.min(i, max));
    setSlideIdx(next);
    applyTransform(next);
  }, [occasions.length, applyTransform]);

  const startAuto = useCallback(() => {
    clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      setSlideIdx(prev => {
        const max = Math.max(0, occasions.length - getPerView());
        const next = prev >= max ? 0 : prev + 1;
        applyTransform(next);
        return next;
      });
    }, 3200);
  }, [occasions.length, applyTransform]);

  useEffect(() => { startAuto(); return () => clearInterval(autoRef.current); }, [startAuto]);

  const perView = getPerView();
  const maxPages = Math.max(1, occasions.length - perView + 1);

  return (
    <section className="occ-sec" id="occasions">
      <div className="container">
        <div className="text-center rv" style={{ marginBottom:52 }}>
          <h2 className="cc-h cc-h-white">Occasions We <span style={{ color:"#C9A84C" }}>Celebrate</span></h2>
          <p className="cc-sub" style={{ color:"rgba(255,255,255,.6)", margin:"10px auto 0" }}>
            Private theatre experiences crafted for your most special moments.
          </p>
        </div>
        <div style={{ position:"relative", overflow:"hidden", padding:"8px 0 12px" }}>
          <div className="occ-track" ref={trackRef}>
            {occasions.length > 0
              ? occasions.map((occ, i) => <OccCard key={i} occ={occ} navigate={navigate} />)
              : <div style={{ padding:"48px 0", color:"rgba(255,255,255,.4)", textAlign:"center" }}>Loading occasions…</div>
            }
          </div>
        </div>
        <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:24 }}>
          {Array.from({ length: maxPages }).map((_, i) => (
            <button key={i} className={`occ-dot${i === slideIdx ? " on" : ""}`}
              onClick={() => { goTo(i); startAuto(); }} />
          ))}
        </div>
        <div style={{ display:"flex", justifyContent:"center", gap:12, marginTop:16 }}>
          <button className="occ-arrow" onClick={() => { goTo(slideIdx - 1); startAuto(); }}>‹</button>
          <button className="occ-arrow" onClick={() => { goTo(slideIdx + 1); startAuto(); }}>›</button>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:12, maxWidth:420, margin:"36px auto 0" }}>
          <a href="/locations" className="occ-cta-book">
            <span className="occ-cta-shine" /><span style={{ fontSize:16 }}>🎉</span>
            <span>Proceed to Book</span><span className="occ-cta-arr">→</span>
          </a>
          <a href="https://wa.me/918341428342?text=Hello%2C%20I%20want%20to%20book%20tickets."
            target="_blank" rel="noopener noreferrer" className="wa-btn" style={{ width:"100%", justifyContent:"center" }}>
            <i className="bi bi-whatsapp" /> Chat With Us
          </a>
        </div>
      </div>
    </section>
  );
}